import { useMemo } from 'react';
import { Check, ChevronRight } from '@mui/icons-material';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useOutlet, useParams } from 'react-router-dom';
import { InvoiceStatus, useBatchUploadStatusQuery } from '../../../../api/gql';
import { ExpandedBox } from '../../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { InfoCard } from '../../../../components/InfoCard';
import { PageHeader } from '../../../../components/PageHeader';
import { PageLayout } from '../../../../layout';
import { DynamicDrawerLayout } from '../../../../layout/dynamic-drawer';
import { theme } from '../../../../style/theme';

const TRANS_NS = 'batch-upload';

export const BatchUploadSummaryPage = () => {
  const outlet = useOutlet();
  const { batchId, invoiceId } = useParams();
  const { t } = useTranslation(TRANS_NS);
  const { data, isLoading, isError } = useBatchUploadStatusQuery(
    { id: batchId ?? '' },
    {
      refetchInterval: 1000,
    },
  );

  const canContinue = useMemo(
    () =>
      data?.batchUpload.invoices &&
      data.batchUpload.invoices.every(
        (invoice) =>
          ![InvoiceStatus.Processing, InvoiceStatus.MissingRequiredData, InvoiceStatus.AwaitingVerification].includes(
            invoice.status,
          ),
      ),
    [data],
  );

  return (
    <PageLayout>
      <PageHeader title={t('title')} subtitle={t('subtitle')} backRouteTo={'/upload'} />
      <DynamicDrawerLayout open={!!outlet} drawerContent={<Outlet />}>
        {isLoading ? <FullSizeLoader /> : null}
        {isError ? (
          <ExpandedBox>
            <Typography>{t('query.error')}</Typography>{' '}
          </ExpandedBox>
        ) : null}
        <Paper
          sx={{
            py: 2,
            px: 4,
            mt: 4,
          }}
        >
          <Typography variant={'h3'}>{t('infobox.title')}</Typography>
          <Typography whiteSpace={'break-spaces'} mt={2}>
            {t('infobox.description')}
          </Typography>
        </Paper>
        <Stack gap={2} sx={{ pt: 4 }}>
          {data?.batchUpload.invoices
            .sort((a, b) => (a?.createdAt && b?.createdAt ? a.createdAt - b.createdAt : 0))
            .map((invoice, i) => (
              <BatchUploadInvoiceInfoCard
                key={invoice.id}
                title={t('invoiceNumber', { number: invoice.number ?? i + 1 })}
                status={invoice.status}
                id={invoice.id}
                batchId={batchId ?? ''}
                highlight={invoice.id === invoiceId}
              />
            ))}
        </Stack>
        <Box mt={4} display={'flex'} justifyContent={'flex-end'}>
          <Button
            disabled={!canContinue}
            startIcon={<Check />}
            variant={'contained'}
            size={'large'}
            component={Link}
            to={`/upload/batch/${batchId}/billing`}
          >
            {t('submit')}
          </Button>
        </Box>
      </DynamicDrawerLayout>
    </PageLayout>
  );
};

type BatchUploadInvoiceInfoCardProps = {
  title: string;
  status: InvoiceStatus;
  id: string;
  batchId: string;
  highlight?: boolean;
};
const ICON_SIZE = 32;
const BatchUploadInvoiceInfoCard = ({ title, status, batchId, id, highlight }: BatchUploadInvoiceInfoCardProps) => {
  const { t } = useTranslation(TRANS_NS);
  const confirmUrl = `/upload/batch/${batchId}/confirm/${id}`;

  const details = useMemo(() => {
    switch (status) {
      case InvoiceStatus.Processing: {
        return {
          icon: (
            <img alt={'icon'} src={'/assets/img/scanning-icon.gif'} style={{ width: ICON_SIZE, height: ICON_SIZE }} />
          ),
          subtitle: t('processing'),
          action: (
            <Button variant={'outlined'} disabled={true} endIcon={<ChevronRight />}>
              {t('checkInvoice')}
            </Button>
          ),
        };
      }
      case InvoiceStatus.MissingRequiredData:
      case InvoiceStatus.AwaitingVerification: {
        return {
          icon: <ContentPasteSearchIcon color={'warning'} sx={{ fontSize: ICON_SIZE }} />,
          subtitle: t('awaitingVerification'),
          action: (
            <Button
              disabled={highlight}
              variant={'contained'}
              component={NavLink}
              to={confirmUrl}
              endIcon={<ChevronRight />}
            >
              {t('checkInvoice')}
            </Button>
          ),
        };
      }
      default: {
        return {
          icon: <Check color={'success'} sx={{ fontSize: ICON_SIZE }} />,
          subtitle: t('completed'),
          action: (
            <Button variant={'outlined'} component={NavLink} to={confirmUrl} endIcon={<ChevronRight />}>
              {t('checkInvoiceAgain')}
            </Button>
          ),
        };
      }
    }
  }, [status, t, highlight, confirmUrl]);

  return (
    <InfoCard
      icon={details.icon}
      style={{ width: '100%' }}
      title={title}
      subtitle={details.subtitle}
      action={
        <Box flex={1} display={'flex'} justifyContent={'flex-end'}>
          {details.action}
        </Box>
      }
      sx={{
        outline: highlight ? `2px solid ${theme.palette.primary.main}` : undefined,
      }}
    />
  );
};
